import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Handshake, MessageSquare, RefreshCw } from "lucide-react";
import { Alert, Button, Chip, Pagination, EmptyState } from "@/components/ui";
import { PageFrame, ListResultsCard } from "@/components/common";
import { BookingsListCardTitle, FilterChipCount } from "@/components/bookings";
import { useDeferredFilters } from "@/hooks/useDeferredFilters";
import {
  HandshakeListItem,
  HandshakeFilters,
  adminHandshakeDestinationPath,
  defaultFilters,
  countHandshakeActiveFilters,
  handshakeSessionStatusLabel,
  type SellerFilterSource,
} from "@/components/handshake";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import type { HandshakeSessionStatus } from "@/types";
import { handshakeKeys, handshakeService } from "@/services/handshakeService";
import { adminService } from "@/services/adminService";

/** Quick status tabs, pending first: what admins check most often. */
const QUICK_STATUSES: readonly HandshakeSessionStatus[] = [
  "PENDING",
  "EXPIRED",
  "CONFIRMED",
  "CANCELLED",
];
const PENDING_COUNT_PARAMS = {
  statuses: ["PENDING" as HandshakeSessionStatus],
  skip: 0,
  limit: 1,
};

export default function AdminHandshakePage() {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t } = useTranslation("handshakePages");
  const { t: tErrors } = useTranslation("errors");
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    formFilters,
    appliedFilters,
    setFormFilters,
    applyFilters,
    applyFilterPatch,
    resetFilters,
    hasUnappliedChanges,
  } = useDeferredFilters(() => defaultFilters, {
    onApply: () => setPage(1),
    onReset: () => setPage(1),
  });

  const sellersQuery = useQuery({
    queryKey: ["admin", "sellers"],
    queryFn: () => adminService.getSellers(),
  });
  const { data: sellers = [] } = sellersQuery;

  const apiParams = useMemo(() => {
    const params: Parameters<typeof handshakeService.adminListSessions>[0] = {
      skip: (page - 1) * limit,
      limit,
    };

    if (appliedFilters.statuses.length > 0) {
      params.statuses = appliedFilters.statuses;
    }
    if (appliedFilters.channels.length > 0) {
      params.channels = appliedFilters.channels;
    }
    if (appliedFilters.dateFrom) {
      params.date_from = new Date(appliedFilters.dateFrom).toISOString();
    }
    if (appliedFilters.dateTo) {
      const endDate = new Date(appliedFilters.dateTo);
      endDate.setHours(23, 59, 59, 999);
      params.date_to = endDate.toISOString();
    }
    if (appliedFilters.sellerUserId) {
      params.seller_user_id = appliedFilters.sellerUserId;
    }

    return params;
  }, [appliedFilters, page, limit]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: handshakeKeys.adminList(apiParams),
    queryFn: () => handshakeService.adminListSessions(apiParams),
    placeholderData: keepPreviousData,
  });

  const { data: pendingData } = useQuery({
    queryKey: handshakeKeys.adminList(PENDING_COUNT_PARAMS),
    queryFn: () => handshakeService.adminListSessions(PENDING_COUNT_PARAMS),
  });
  const pendingTotal = pendingData?.total ?? 0;

  const quickStatus: HandshakeSessionStatus | "all" | null =
    appliedFilters.statuses.length === 0
      ? "all"
      : appliedFilters.statuses.length === 1
        ? appliedFilters.statuses[0]
        : null;

  const sessions = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);
  const showLoadErrorEmpty = isError && sessions.length === 0;
  const loadFailed = isError && sessions.length > 0;

  const hasActiveFilters = useMemo(
    () =>
      countHandshakeActiveFilters(appliedFilters, {
        showCustomerNameFilter: false,
        showSellerFilter: true,
      }) > 0,
    [appliedFilters],
  );

  const sellerFilter: SellerFilterSource = {
    sellers: sellers.map((p) => ({ id: p.user_id, name: p.name })),
    isLoading: sellersQuery.isLoading,
    loadError: queryLoadFailure(sellersQuery, tErrors("loadSellers")),
  };

  return (
    <PageFrame
      title={t("list.adminTitle")}
      description={t("list.adminDescription")}
      icon={Handshake}
      actions={
        <Button
          variant="outline"
          isLoading={isFetching}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() =>
            void queryClient.invalidateQueries({
              queryKey: handshakeKeys.adminLists(),
            })
          }
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <ListResultsCard
        headerClassName="px-5 py-3"
        contentClassName="p-3 sm:p-5"
        title={
          <BookingsListCardTitle
            icon={MessageSquare}
            title={t("list.conversationsFound", { count: total })}
          />
        }
        beforeContent={
          <div className="space-y-3 border-b border-surface-200/70 bg-surface-50/60 px-5 py-3 dark:bg-surface-100/30">
            <div
              className="-mx-5 overflow-x-auto px-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="group"
              aria-label={t("list.quickStatusLabel")}
            >
              <div className="flex w-max gap-2">
                <Chip
                  selected={quickStatus === "all"}
                  onClick={() => applyFilterPatch({ statuses: [] })}
                  className="shrink-0 whitespace-nowrap"
                >
                  {t("list.quickStatusAll")}
                </Chip>
                {QUICK_STATUSES.map((status) => (
                  <Chip
                    key={status}
                    selected={quickStatus === status}
                    onClick={() => applyFilterPatch({ statuses: [status] })}
                    className="shrink-0 whitespace-nowrap"
                  >
                    <span>{handshakeSessionStatusLabel(status)}</span>
                    {status === "PENDING" ? (
                      <FilterChipCount
                        count={pendingTotal}
                        selected={quickStatus === status}
                      />
                    ) : null}
                  </Chip>
                ))}
              </div>
            </div>
            <HandshakeFilters
              formFilters={formFilters}
              appliedFilters={appliedFilters}
              setFormFilters={setFormFilters}
              applyFilters={applyFilters}
              resetFilters={resetFilters}
              hasUnappliedChanges={hasUnappliedChanges}
              showCustomerNameFilter={false}
              sellerFilter={sellerFilter}
            />
          </div>
        }
        isLoading={isLoading}
        loadingMessage={t("list.loadingConversations")}
        isEmpty={
          !isLoading && (showLoadErrorEmpty || sessions.length === 0)
        }
        emptyState={
          showLoadErrorEmpty ? (
            <EmptyState
              variant="dashedWarning"
              icon={AlertCircle}
              title={t("list.loadFailedTitle")}
              description={t("list.loadFailedHint")}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetch()}
                >
                  {t("list.loadFailedRetry")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              variant="dashed"
              icon={MessageSquare}
              title={t("list.noConversationsFound")}
              description={
                hasActiveFilters
                  ? t("list.tryAdjustingFilters")
                  : t("list.emptyHintAdmin")
              }
              action={
                hasActiveFilters ? (
                  <Button variant="outline" onClick={resetFilters} size="sm">
                    {t("list.clearFilters")}
                  </Button>
                ) : undefined
              }
            />
          )
        }
        pagination={
          totalPages > 1 ? (
            <Pagination
              labels={tableLabels}
              className="mt-5"
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              showPageNumbers
              isLoading={isFetching}
              total={total}
            />
          ) : null
        }
      >
        <>
          {loadFailed ? (
            <Alert
              variant="warning"
              className="mb-4 bg-warning-50/60"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 self-start sm:self-auto"
                  onClick={() => void refetch()}
                >
                  {t("list.loadFailedRetry")}
                </Button>
              }
            >
              {t("list.loadFailedStale")}
            </Alert>
          ) : null}
          <ul className="divide-y divide-surface-200/70 overflow-hidden rounded-lg border border-surface-200">
            {sessions.map((session) => (
              <HandshakeListItem
                key={session.id}
                session={session}
                to={adminHandshakeDestinationPath({
                  booking_id: session.booking_id,
                  seller_user_id: session.seller_user_id,
                })}
              />
            ))}
          </ul>
        </>
      </ListResultsCard>
    </PageFrame>
  );
}
